import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { format, isValid } from "date-fns";

import DomainPill from "components/ClientDetails/Sections/GoalsAndTargets/shared/DomainPill";
import StatusPill from "components/ClientDetails/Sections/GoalsAndTargets/shared/StatusPill";

import type { Status as StatusId, GoalDomain as DomainId } from "api/types/aba";
import type { ClinicalPhase } from "../../shared/types";
import { BLOCK_BORDER_COLOR } from "../../shared/constants";
import ClinicalPhasePill from "../../shared/ClinicalPhasePill";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export type GoalType = "skillAcquisition" | "behaviorReduction";
export type BehaviorSubject = "client" | "caregiver";

export interface GoalOverviewCardProps {
  /** Goal title */
  title: string;
  /** Clinical domain of the goal */
  domain?: DomainId;
  /** Current clinical phase of the goal */
  clinicalPhase?: ClinicalPhase;
  /** Current status of the goal */
  status: StatusId;
  /** Goal type: skill acquisition or behavior reduction */
  goalType: GoalType;
  /** Whose behavior is being measured */
  behaviorMeasured: BehaviorSubject;
  /** Whether the target is a replacement behavior / FCR — row hidden when undefined (goals, behavior-reduction targets) */
  replacementBehavior?: boolean;
  /** Display label for the primary implementer — row hidden when undefined */
  implementerLabel?: string;
  /** Display label for the implementation setting — row hidden when undefined */
  settingLabel?: string;
  /** Date of the last session, or null/undefined if not yet started */
  lastSessionDate?: Date | string | null;
  /** Additional sx styles for the card container */
  sx?: SxProps<Theme>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const GOAL_TYPE_LABELS: Record<GoalType, string> = {
  skillAcquisition: "Skill Acquisition",
  behaviorReduction: "Behavior Reduction",
};

const BEHAVIOR_SUBJECT_LABELS: Record<BehaviorSubject, string> = {
  client: "Client",
  caregiver: "Caregiver",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatLastSession(date: Date | string | null | undefined): string {
  if (!date) return "—";
  const d = typeof date === "string" ? new Date(date) : date;
  return isValid(d) ? format(d, "EEE, MMMM do yyyy") : "—";
}

// ---------------------------------------------------------------------------
// Internal sub-components
// ---------------------------------------------------------------------------
interface InfoRowProps {
  label: string;
  children: React.ReactNode;
}

function InfoRow({ label, children }: InfoRowProps) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {children}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// GoalOverviewCard
// ---------------------------------------------------------------------------
/**
 * @component GoalOverviewCard
 * Sidebar card summarising key metadata for a goal: domain, status, type,
 * behavior subject, last session date, and optional resource links.
 *
 * Designed for the sticky right sidebar in the Goal Detail view.
 *
 * @param props
 * @param props.title            - Goal title (required for good UX, but not enforced)
 * @param props.domain           - Clinical domain of the goal
 * @param props.clinicalPhase    - Clinical phase of the goal (if applicable)
 * @param props.status           - Current goal status
 * @param props.goalType         - Skill acquisition or behavior reduction
 * @param props.behaviorMeasured - Whose behavior is being tracked
 * @param props.lastSessionDate  - Most recent session date (undefined = not started)
 * @param props.sx               - Additional MUI sx overrides for the card
 */
const GoalOverviewCard: React.FC<GoalOverviewCardProps> = ({
  title,
  domain,
  clinicalPhase,
  status,
  goalType,
  behaviorMeasured,
  replacementBehavior,
  implementerLabel,
  settingLabel,
  lastSessionDate,
  sx,
}) => {
  return (
    <Stack
      component="section"
      aria-label={title}
      direction="column"
      gap={4}
      p={5}
      sx={[
        {
          bgcolor: "background.paper",
          border: `1px solid ${BLOCK_BORDER_COLOR}`,
          borderRadius: "12px",
          width: "100%",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {/* Heading */}
      <Typography component="h2" variant="body2">
        {title}
      </Typography>

      {/* Info rows */}
      <Stack direction="column" gap={4}>
        {domain ? (
          <InfoRow label="Domain">
            <DomainPill domain={domain} />
          </InfoRow>
        ) : null}

        {clinicalPhase ? (
          <InfoRow label="Phase">
            <ClinicalPhasePill phase={clinicalPhase} />
          </InfoRow>
        ) : null}

        <InfoRow label="Status">
          <StatusPill status={status} />
        </InfoRow>

        <InfoRow label="Type">
          <Typography variant="caption" color="text.primary">
            {GOAL_TYPE_LABELS[goalType]}
          </Typography>
        </InfoRow>

        {replacementBehavior !== undefined ? (
          <InfoRow label="Replacement Behavior">
            <Typography variant="caption" color="text.primary">
              {replacementBehavior ? "Yes" : "No"}
            </Typography>
          </InfoRow>
        ) : null}

        <InfoRow label="Behavior Measured">
          <Typography variant="caption" color="text.primary">
            {BEHAVIOR_SUBJECT_LABELS[behaviorMeasured]}
          </Typography>
        </InfoRow>

        {implementerLabel ? (
          <InfoRow label="Implementer">
            <Typography variant="caption" color="text.primary">
              {implementerLabel}
            </Typography>
          </InfoRow>
        ) : null}

        {settingLabel ? (
          <InfoRow label="Setting">
            <Typography variant="caption" color="text.primary">
              {settingLabel}
            </Typography>
          </InfoRow>
        ) : null}

        <InfoRow label="Last Session">
          <Typography variant="caption" color="text.primary">
            {formatLastSession(lastSessionDate)}
          </Typography>
        </InfoRow>
      </Stack>
    </Stack>
  );
};

export default GoalOverviewCard;
